
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { Search, UserPlus, Mail } from "lucide-react";
import { User, FriendWithStatus } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { createInvite } from "@/integrations/supabase/inviteService";
import { notifyFriendInvite } from "@/utils/notificationUtils";

interface AddFriendModalProps {
  isOpen: boolean;
  onClose: () => void;
  friends: FriendWithStatus[];
  pendingRequests: FriendWithStatus[];
  sentRequests: FriendWithStatus[];
  sendFriendRequest: (friendId: string) => Promise<unknown>;
  refreshFriendships: () => Promise<void>;
}

const AddFriendModal = ({
  isOpen,
  onClose,
  friends,
  pendingRequests,
  sentRequests,
  sendFriendRequest,
  refreshFriendships
}: AddFriendModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const { user } = useAuth();

  // Helper to check friendship status
  const getFriendshipStatus = (userId: string) => {
    if (friends.some(f => f.id === userId)) return "friends";
    if (pendingRequests.some(f => f.id === userId)) return "pending-received";
    if (sentRequests.some(f => f.id === userId)) return "pending-sent";
    return null;
  };

  const handleSearch = async () => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      toast.error("Please enter an email address");
      return;
    }

    setIsSearching(true);
    setInviteSent(false);

    try {
      let query = supabase
        .from('profiles')
        .select('*')
        .eq('email', trimmed);

      if (user) {
        query = query.neq('id', user.id);
      }

      const { data: profiles, error } = await query.limit(5);

      if (error) throw error;

      const users: User[] = (profiles || []).map(profile => ({
        id: profile.id,
        name: profile.name || `User ${profile.id.substring(0, 4)}`,
        email: profile.email || null,
        imageUrl: profile.image_url || null
      }));

      setSearchResults(users);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching users:", error);
      toast.error("Failed to search users");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddFriend = async (friendUser: User) => {
    await sendFriendRequest(friendUser.id);
    // Fire-and-forget: notify the friend via email
    if (friendUser.email) {
      notifyFriendInvite({
        type: 'friend_request',
        recipientEmail: friendUser.email,
        recipientName: friendUser.name,
      });
    }
    await refreshFriendships();
  };

  const handleInvite = async () => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) return;

    setIsInviting(true);
    try {
      const { error } = await createInvite(trimmed);
      if (error) {
        toast.error("Failed to send invite");
      } else {
        setInviteSent(true);
        toast.success("Invite sent!");
      }
    } catch {
      toast.error("Failed to send invite");
    } finally {
      setIsInviting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setSearchResults([]);
    setHasSearched(false);
    setInviteSent(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Friend</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Enter friend's email address"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setInviteSent(false); setHasSearched(false); }}
                onKeyDown={handleKeyDown}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={isSearching}
              className="bg-forest hover:bg-forest/90"
              aria-label="Search"
            >
              {isSearching ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div className="max-h-72 overflow-y-auto space-y-3">
            {searchResults.length > 0 ? (
              searchResults.map(resultUser => {
                const status = getFriendshipStatus(resultUser.id);

                return (
                  <div key={resultUser.id} className="border rounded-md p-3 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <AvatarPlaceholder user={resultUser} />
                      <div>
                        <p className="font-medium">{resultUser.name}</p>
                        {resultUser.email && <p className="text-sm text-muted-foreground">{resultUser.email}</p>}
                      </div>
                    </div>

                    {status === "friends" ? (
                      <Button variant="outline" disabled className="text-sm">
                        Already Friends
                      </Button>
                    ) : status === "pending-received" ? (
                      <Button variant="outline" disabled className="text-sm">
                        Request Received
                      </Button>
                    ) : status === "pending-sent" ? (
                      <Button variant="outline" disabled className="text-sm">
                        Request Sent
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleAddFriend(resultUser)}
                        className="bg-forest hover:bg-forest/90"
                        size="sm"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    )}
                  </div>
                );
              })
            ) : hasSearched && !isSearching ? (
              <div className="text-center py-8">
                {inviteSent ? (
                  <>
                    <Mail className="h-8 w-8 mx-auto text-forest mb-2" />
                    <p className="text-forest font-medium">Invite sent!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      We'll notify you when they join.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-muted-foreground mb-3">No account found for this email</p>
                    <Button
                      onClick={handleInvite}
                      disabled={isInviting}
                      className="bg-forest hover:bg-forest/90"
                    >
                      {isInviting ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      Invite {searchTerm.trim()} to Birdie Chase
                    </Button>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddFriendModal;
